import React, { useEffect, useMemo, useState } from 'react';

import { eventService } from '../../services/eventService';
import { useAppDispatch } from '../../store/hooks';
import { api } from '../../store/services/api';
import {
  type PlanningStartAction, type PlanningStartActionQuote, type PlanningStartQuote, useGetPlanningStartQuoteQuery,
} from '../../store/services/planningStartQuoteApi';
import { getTopUpCreditAmount } from '../../utils/creditGate';
import { openReferEarn } from '../../utils/freeCreditsPrompts';
import { formatPhaseCredits } from '../../utils/phaseBuildPresentation';
import { BillingModal } from '../BillingModal';

import { ActionButtonLabel } from './ActionBarActions';
import './PlanningStartCreditActions.css';

/** A balance too low to start any offered run button: lead with credit actions, keep the run secondary. */
export interface PlanningStartCredit {
  /** The cheapest offered start; the note names it. */
  required: number;
  balance: number;
  shortfall: number;
  openEnded: boolean;
  upgradeAvailable: boolean;
  /** An organization's pool pays: its owner adds credits, so the member gets no Add Credits / Upgrade. */
  orgPool: boolean;
  /** "Needs X · you have Y", worded by the server in the language of the message the bar sits under. */
  note: string;
  /** Per-button tooltip: what that button needs, and that pressing it checks the balance again. */
  noteFor: (key: string) => string | undefined;
}

const CREDIT_EVENTS = ['credits_update', 'insufficient_credits', 'planning_complete'] as const;
const BILLING_EVENTS = ['billing_topup_success', 'billing_upgrade_success', 'credits_update'];

// Needs rounds up and the balance rounds down, so the note never reads "needs 0.2 · you have 0.2".
const needs = (value: number): string => formatPhaseCredits(Math.ceil(value * 100 - 1e-9) / 100);
const have = (value: number): string => formatPhaseCredits(Math.floor(value * 100 + 1e-9) / 100);

/** The English note, for a quote without the server's wording; a balance below zero says how far short it is. */
export const planningStartCreditHint = (
  { required, balance, openEnded }: Pick<PlanningStartCredit, 'required' | 'balance' | 'openEnded'>,
): string => {
  const need = `${openEnded ? 'at least ' : ''}${needs(required)} credits`;

  return balance >= 0 ? `Needs ${need} · you have ${have(balance)}` : `Needs ${need} · you are ${needs(required - balance)} credits short`;
};

/** Short only when every offered button needs more than the balance; an affordable one keeps the bar as it is. */
export const planningStartCredit = (
  quote: PlanningStartQuote | undefined,
  keys: string[],
): PlanningStartCredit | null => {
  if (!quote || quote.unlimited) return null;
  const quoted = keys.map((key) => quote.actions[key]).filter(Boolean);

  if (!quoted.length || quoted.some((action) => !(action.shortfall > 0))) return null;
  const cheapest = quoted.reduce((low, action) => (action.required < low.required ? action : low));
  const fallback = (action: PlanningStartActionQuote) => planningStartCreditHint({
    required: action.required, balance: quote.balance, openEnded: action.open_ended,
  });

  return {
    required: cheapest.required,
    balance: quote.balance,
    shortfall: cheapest.shortfall,
    openEnded: cheapest.open_ended,
    upgradeAvailable: quote.upgrade_available,
    orgPool: quote.org_pool,
    note: cheapest.note ?? fallback(cheapest),
    noteFor: (key) => {
      const action = quote.actions[key];

      return action ? action.tooltip ?? `${fallback(action)}. Pressing it checks the balance again.` : undefined;
    },
  };
};

/**
 * Live start quote for the offered run buttons. Credit and billing events refetch it, and so does returning to
 * the tab (a daily grant may have landed meanwhile), so a stale "short" never outlives the balance it was read
 * against. Nothing starts here: the run button stays pressable and the server decides.
 */
export const usePlanningStartCredit = (
  projectId: string | null | undefined,
  actions: PlanningStartAction[],
  active: boolean,
  entryId?: string | null,
): PlanningStartCredit | null => {
  const dispatch = useAppDispatch();
  const actionsKey = JSON.stringify(actions);
  const body = useMemo(() => JSON.parse(actionsKey) as PlanningStartAction[], [actionsKey]);
  const skip = !active || !projectId || body.length === 0;
  const { currentData } = useGetPlanningStartQuoteQuery(
    { projectId: projectId ?? '', actions: body, entryId },
    { skip, refetchOnMountOrArgChange: 30 },
  );

  useEffect(() => {
    if (skip) return undefined;
    const refresh = () => {
      dispatch(api.util.invalidateTags([{ type: 'PlanningStartQuote', id: projectId ?? '' }]));
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const unsubscribers = CREDIT_EVENTS.map((name) => eventService.subscribe(name, refresh));

    BILLING_EVENTS.forEach((name) => window.addEventListener(name, refresh));
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      BILLING_EVENTS.forEach((name) => window.removeEventListener(name, refresh));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dispatch, projectId, skip]);

  return useMemo(
    () => (skip ? null : planningStartCredit(currentData, body.map((action) => action.key))),
    [body, currentData, skip],
  );
};

const CreditNote: React.FC<{ note: string }> = ({ note }) => (
  <span className="ai-overview-action-note" data-action-layout-message="true" data-action-mirror-note="true"
    role="status">{note}</span>
);

/**
 * The credit actions a short balance puts ahead of a run button: Add Credits, then Upgrade (or Free credits on a
 * plan with nothing bigger), and the "needs X · you have Y" note. The --add-credit / --upgrade classes pin them
 * to the bar and give them its attention ring; the run button stays beside them as the secondary choice. On an
 * organization's pool only the note shows: the member's own wallet would not pay for the press.
 */
export const PlanningStartCreditActions: React.FC<{ credit: PlanningStartCredit }> = ({ credit }) => {
  const [billing, setBilling] = useState<'credits' | 'plans' | null>(null);

  if (credit.orgPool) return <CreditNote note={credit.note} />;

  return (
    <>
      <button type="button" className="ai-overview-action-btn ai-overview-action-btn--add-credit"
        data-action-key="start-credit-add" onClick={() => setBilling('credits')}>
        <ActionButtonLabel label="Add Credits" />
      </button>
      {credit.upgradeAvailable ? (
        <button type="button" className="ai-overview-action-btn ai-overview-action-btn--upgrade"
          data-action-key="start-credit-upgrade" onClick={() => setBilling('plans')}>
          <ActionButtonLabel label="Upgrade" />
        </button>
      ) : (
        <button type="button" className="ai-overview-action-btn ai-overview-action-btn--free-credits"
          data-action-key="start-credit-free" data-action-placement="pinned" onClick={openReferEarn}>
          <ActionButtonLabel label="Free credits" />
        </button>
      )}
      <CreditNote note={credit.note} />
      {billing && (
        <BillingModal
          initialView={billing}
          initialCreditAmount={billing === 'credits' ? getTopUpCreditAmount({ shortfall: credit.shortfall }) : undefined}
          onClose={() => setBilling(null)}
        />
      )}
    </>
  );
};
